import { createSelector } from '@reduxjs/toolkit';

import { fileChannelsSortFunction } from 'src/legacy/components/Files/utilities';
import { RootState } from 'src/store/reduxTypes';
import { arrayToMap } from 'src/utils/array';

export const selectActiveFileChannel = createSelector(
  [
    (state: RootState) => state.files.channels,
    (_: RootState, channelId: string) => channelId,
  ],
  (channels, channelId) => channels.find((c) => c.id === channelId),
);

export const selectFilesChannels = createSelector(
  [
    (state: RootState) => state.files.channels,
    (state: RootState) => state.notifications,
    (state: RootState) => state.activeCompany.activeCompanyId,
    (state: RootState) => state.user.isClient,
  ],
  (channels, notifications, activeCompanyId, isClient) =>
    channels.sort(fileChannelsSortFunction(notifications)).filter((c) => {
      if (isClient && activeCompanyId && c.fields.companyID !== '') {
        return c.fields.companyID === activeCompanyId;
      }
      return true;
    }),
);

export const selectUsersMetaDataMap = createSelector(
  [
    (state: RootState) => state.users.users,
    (state: RootState) => state.user.isClient,
    (state: RootState) => state.clients.activeClients,
  ],
  (users, isClient, clients) =>
    arrayToMap(
      (isClient ? users : clients).map((u) => ({
        id: u.id,
        givenName: u.fields?.givenName,
        familyName: u.fields?.familyName,
        email: u.fields?.email,
        avatarImageUrl: u.fields?.avatarImageUrl,
        fallbackColor: u.fields?.fallbackColor,
        companyIds: u.fields?.companyIds || [],
      })),
      'id',
    ),
);

export const selectCompaniesMetaDataMap = createSelector(
  [
    (state: RootState) => state.clients.activeCompanies,
    (state: RootState) => state.clients.companies,
    (state: RootState) => state.user.isClient,
  ],
  (activeCompanies, allCompanies, isClient) =>
    arrayToMap(
      (isClient ? allCompanies : activeCompanies).map((c) => ({
        id: c.id,
        name: c.fields?.name,
        avatarImageURL: c.fields?.avatarImageURL,
        fallbackColor: c.fields?.fallbackColor,
      })),
      'id',
    ),
);

export const getSelectedRowIds = (state: RootState) =>
  state.files.selectedRowIds;

export const getSelectionCount = (state: RootState) =>
  state.files.selectedRowIds.size;

export const selectFileActionSettings = createSelector(
  (state: RootState) => state.settings.fileSettings,
  (state: RootState) => state.user.isClient,
  (fileSettings, isClient) => {
    const disableFolderCreate =
      isClient && Boolean(fileSettings?.clientFolderUpdateDisabled);
    const disableAddFile =
      isClient && Boolean(fileSettings?.clientFileAddDisabled);
    const disableFileUpdate =
      isClient && Boolean(fileSettings?.clientFileUpdateDisabled);

    return {
      disableFolderCreate,
      disableAddFile,
      disableFileUpdate,
    };
  },
);
